import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { Plus, X, ArrowUp, ArrowDown, Trash2, Palette } from 'lucide-react';
import FilterBuilder from './FilterBuilder';

// Color palettes for pie charts
const COLOR_PALETTES = {
  vibrant: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1'],
  pastel: ['#A5D8FF', '#A7F3D0', '#FDE68A', '#FCA5A5', '#C4B5FD', '#67E8F9', '#F9A8D4', '#D9F99D', '#FDBA74', '#C7D2FE'],
  monochrome: ['#1F2937', '#374151', '#4B5563', '#6B7280', '#9CA3AF', '#D1D5DB', '#E5E7EB', '#F3F4F6', '#F9FAFB', '#FFFFFF'],
  warm: ['#F97316', '#EF4444', '#DC2626', '#F59E0B', '#FBBF24', '#FCD34D', '#FED7AA', '#FFEDD5', '#FFF7ED', '#FEF2F2'],
  cool: ['#3B82F6', '#2563EB', '#1D4ED8', '#10B981', '#059669', '#06B6D4', '#0891B2', '#0EA5E9', '#60A5FA', '#93C5FD'],
  sunset: ['#F97316', '#EC4899', '#8B5CF6', '#6366F1', '#3B82F6', '#10B981', '#84CC16', '#FBBF24', '#F59E0B', '#EF4444']
};

// Default colors for bar/line charts
const DEFAULT_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1'
];

const ChartEditor = ({ entityName, config, onChange, componentType }) => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (entityName) {
      loadFields();
    }
  }, [entityName]);

  // Initialize config with defaults
  useEffect(() => {
    const defaultConfig = {
      chartType: componentType === 'BarChart' ? 'bar' : componentType === 'LineChart' ? 'line' : 'pie',
      xAxis: config.xAxis || null,
      series: config.series || [],
      grouping: config.grouping || null,
      sorting: config.sorting || { field: null, direction: 'asc' },
      filters: config.filters || [],
      barOrientation: config.barOrientation || (componentType === 'BarChart' ? 'vertical' : undefined)
    };
    
    if (JSON.stringify(defaultConfig) !== JSON.stringify(config)) {
      onChange(defaultConfig);
    }
  }, []);

  const loadFields = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/entity-metadata/${entityName}/fields`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load fields');
      }

      const data = await response.json();
      setFields(data.data || []);
    } catch (error) {
      console.error('Error loading fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSeries = () => {
    const existingSeriesCount = (config.series || []).length;
    const newSeries = {
      id: Date.now(),
      field: null,
      label: '',
      aggregation: 'COUNT', // COUNT, SUM, AVG, MIN, MAX - default to COUNT as it works on any field
      color: DEFAULT_COLORS[existingSeriesCount % DEFAULT_COLORS.length]
    };
    onChange({
      ...config,
      series: [...(config.series || []), newSeries]
    });
  };

  const handleUpdateSeries = (seriesId, key, value) => {
    onChange({
      ...config,
      series: config.series.map(s => 
        s.id === seriesId ? { ...s, [key]: value } : s
      )
    });
  };

  const handleRemoveSeries = (seriesId) => {
    onChange({
      ...config,
      series: config.series.filter(s => s.id !== seriesId)
    });
  };

  const handleMoveSeries = (index, direction) => {
    const newSeries = [...config.series];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newSeries.length) return;
    
    [newSeries[index], newSeries[newIndex]] = [newSeries[newIndex], newSeries[index]];
    onChange({
      ...config,
      series: newSeries
    });
  };

  const getNumericFields = () => {
    return fields.filter(f =>
      ['integer', 'decimal', 'number', 'currency', 'int', 'float', 'double', 'bigint'].includes(f.type?.toLowerCase())
    );
  };

  const getCategoricalFields = () => {
    return fields.filter(f =>
      ['string', 'varchar', 'nvarchar', 'text', 'date', 'datetime', 'timestamp'].includes(f.type?.toLowerCase())
    );
  };

  const isPieChart = config.chartType === 'pie' || componentType === 'PieChart';
  const isBarChart = config.chartType === 'bar' || componentType === 'BarChart';
  const isLineChart = config.chartType === 'line' || componentType === 'LineChart';

  // For pie charts, xAxis is used as the category field
  if (isPieChart && !config.xAxis && config.series?.[0]?.field) {
    // Auto-set xAxis from first available categorical field if not set
    const categoricalFields = getCategoricalFields();
    if (categoricalFields.length > 0 && !config.xAxis) {
      // Don't auto-set, let user choose
    }
  }

  return (
    <div className="space-y-6">
      {/* Chart Type (if componentType allows multiple) */}
      {componentType === 'Chart' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Chart Type
          </label>
          <select
            value={config.chartType || 'bar'}
            onChange={(e) => onChange({ ...config, chartType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="bar">Bar Chart</option>
            <option value="line">Line Chart</option>
            <option value="pie">Pie Chart</option>
          </select>
        </div>
      )}

      {/* X-Axis Configuration */}
      {!isPieChart && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            X-Axis (Category Field) <span className="text-red-500">*</span>
          </label>
          <select
            value={config.xAxis || ''}
            onChange={(e) => {
              const selectedField = fields.find(f => f.name === e.target.value);
              const isDateField = selectedField && ['date', 'datetime', 'timestamp'].includes(selectedField.type?.toLowerCase());
              onChange({ 
                ...config, 
                xAxis: e.target.value,
                // Reset dateFormat if not a date field
                dateFormat: isDateField ? (config.dateFormat || 'date') : undefined
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            required
          >
            <option value="">Select X-Axis Field</option>
            {getCategoricalFields().map(field => (
              <option key={field.name} value={field.name}>
                {field.label || field.name} ({field.type})
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            The field used for categories on the X-axis (e.g., date, category, name)
          </p>
          
          {/* Date Format Options */}
          {config.xAxis && (() => {
            const selectedField = fields.find(f => f.name === config.xAxis);
            const isDateField = selectedField && ['date', 'datetime', 'timestamp'].includes(selectedField.type?.toLowerCase());
            
            if (isDateField) {
              return (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Date Format
                  </label>
                  <select
                    value={config.dateFormat || 'date'}
                    onChange={(e) => onChange({ ...config, dateFormat: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="date">Date (YYYY-MM-DD)</option>
                    <option value="month">Month (YYYY-MM)</option>
                    <option value="year">Year (YYYY)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Group data by {config.dateFormat === 'date' ? 'day' : config.dateFormat === 'month' ? 'month' : 'year'}
                  </p>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* Bar Chart Orientation */}
      {isBarChart && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Bar Orientation
          </label>
          <select
            value={config.barOrientation || 'vertical'}
            onChange={(e) => onChange({ ...config, barOrientation: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="vertical">Vertical (Columns)</option>
            <option value="horizontal">Horizontal (Rows)</option>
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {config.barOrientation === 'horizontal' 
              ? 'Bars extend horizontally from left to right'
              : 'Bars extend vertically from bottom to top'}
          </p>
        </div>
      )}

      {/* Category Field for Pie Chart */}
      {isPieChart && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Category Field (Group By) <span className="text-red-500">*</span>
          </label>
          <select
            value={config.xAxis || ''}
            onChange={(e) => {
              const selectedField = fields.find(f => f.name === e.target.value);
              const isDateField = selectedField && ['date', 'datetime', 'timestamp'].includes(selectedField.type?.toLowerCase());
              onChange({ 
                ...config, 
                xAxis: e.target.value,
                // Reset dateFormat if not a date field
                dateFormat: isDateField ? (config.dateFormat || 'date') : undefined
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            required
          >
            <option value="">Select Category Field</option>
            {getCategoricalFields().map(field => (
              <option key={field.name} value={field.name}>
                {field.label || field.name} ({field.type})
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            The field used to group data into pie slices (e.g., status, category, type)
          </p>
          
          {/* Date Format Options for Pie Chart */}
          {config.xAxis && (() => {
            const selectedField = fields.find(f => f.name === config.xAxis);
            const isDateField = selectedField && ['date', 'datetime', 'timestamp'].includes(selectedField.type?.toLowerCase());
            
            if (isDateField) {
              return (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Date Format
                  </label>
                  <select
                    value={config.dateFormat || 'date'}
                    onChange={(e) => onChange({ ...config, dateFormat: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="date">Date (YYYY-MM-DD)</option>
                    <option value="month">Month (YYYY-MM)</option>
                    <option value="year">Year (YYYY)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Group data by {config.dateFormat === 'date' ? 'day' : config.dateFormat === 'month' ? 'month' : 'year'}
                  </p>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* Y-Axis Series Configuration */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {isPieChart ? 'Value Field' : 'Y-Axis Series'} <span className="text-red-500">*</span>
          </label>
          {!isPieChart && (
            <button
              type="button"
              onClick={handleAddSeries}
              className="btn btn-secondary text-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Series
            </button>
          )}
        </div>

        {isPieChart ? (
          // Pie chart: aggregation and value field
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Aggregation Function <span className="text-red-500">*</span>
                </label>
                <select
                  value={config.series?.[0]?.aggregation || 'COUNT'}
                  onChange={(e) => {
                    const series = config.series?.[0] || { id: Date.now(), field: null, label: '', aggregation: 'COUNT' };
                    onChange({
                      ...config,
                      series: [{ ...series, aggregation: e.target.value }]
                    });
                  }}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 font-medium"
                >
                  <option value="COUNT">COUNT</option>
                  <option value="SUM">SUM</option>
                  <option value="AVG">AVG (Average)</option>
                  <option value="MIN">MIN (Minimum)</option>
                  <option value="MAX">MAX (Maximum)</option>
                </select>
              </div>
              <div className="text-2xl text-gray-400 dark:text-gray-500 mt-6">(</div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Field <span className="text-red-500">*</span>
                </label>
                <select
                  value={config.series?.[0]?.field || ''}
                  onChange={(e) => {
                    const series = config.series?.[0] || { id: Date.now(), field: null, label: '', aggregation: config.series?.[0]?.aggregation || 'COUNT' };
                    onChange({
                      ...config,
                      series: [{ ...series, field: e.target.value, label: e.target.value }]
                    });
                  }}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  required
                >
                  <option value="">Select Field</option>
                  {((config.series?.[0]?.aggregation || 'COUNT') === 'COUNT' ? fields : getNumericFields()).map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label || field.name} {field.type ? `(${field.type})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-2xl text-gray-400 dark:text-gray-500 mt-6">)</div>
            </div>
            {config.series?.[0]?.aggregation && config.series?.[0]?.field && (
              <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded px-3 py-2">
                <div className="text-xs font-medium text-teal-800 dark:text-teal-200 mb-1">
                  Expression:
                </div>
                <div className="text-sm font-mono text-teal-900 dark:text-teal-100">
                  {config.series[0].aggregation}({config.series[0].field})
                </div>
                <div className="text-xs text-teal-600 dark:text-teal-400 mt-1">
                  Grouped by: {config.xAxis || 'category field'}
                </div>
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {config.series?.[0]?.aggregation === 'COUNT' 
                ? 'COUNT works on any field type (counts non-null values)'
                : 'SUM, AVG, MIN, MAX require numeric fields'}
            </p>

            {/* Color Palette Selection for Pie Chart */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Palette className="w-4 h-4 inline mr-1" />
                Color Palette Theme
              </label>
              <select
                value={config.colorPalette || 'vibrant'}
                onChange={(e) => onChange({ ...config, colorPalette: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              >
                {Object.keys(COLOR_PALETTES).map(palette => (
                  <option key={palette} value={palette}>
                    {palette.charAt(0).toUpperCase() + palette.slice(1)}
                  </option>
                ))}
              </select>
              <div className="mt-2 flex flex-wrap gap-1">
                {COLOR_PALETTES[config.colorPalette || 'vibrant'].slice(0, 10).map((color, idx) => (
                  <div
                    key={idx}
                    className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select a color palette theme for the pie chart slices
              </p>
            </div>
          </div>
        ) : (
          // Bar/Line chart: multiple series
          <div className="space-y-3">
            {(!config.series || config.series.length === 0) ? (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                No series added. Click "Add Series" to add a data series.
              </div>
            ) : (
              config.series.map((series, index) => (
                <div key={series.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Series {index + 1}
                      </span>
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => handleMoveSeries(index, -1)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Move up"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                      )}
                      {index < config.series.length - 1 && (
                        <button
                          type="button"
                          onClick={() => handleMoveSeries(index, 1)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Move down"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveSeries(series.id)}
                      className="p-1 text-red-400 hover:text-red-600"
                      title="Remove series"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Aggregation Function <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={series.aggregation || 'COUNT'}
                          onChange={(e) => {
                            const newAggregation = e.target.value;
                            handleUpdateSeries(series.id, 'aggregation', newAggregation);
                            // If switching to COUNT and no field selected, suggest a field
                            if (newAggregation === 'COUNT' && !series.field && fields.length > 0) {
                              // Auto-select first available field for COUNT
                              const firstField = fields[0];
                              if (firstField) {
                                handleUpdateSeries(series.id, 'field', firstField.name);
                              }
                            }
                          }}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 font-medium"
                        >
                          <option value="COUNT">COUNT</option>
                          <option value="SUM">SUM</option>
                          <option value="AVG">AVG (Average)</option>
                          <option value="MIN">MIN (Minimum)</option>
                          <option value="MAX">MAX (Maximum)</option>
                        </select>
                      </div>
                      <div className="text-2xl text-gray-400 dark:text-gray-500 mt-6">(</div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Field <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={series.field || ''}
                          onChange={(e) => handleUpdateSeries(series.id, 'field', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                          required
                        >
                          <option value="">Select Field</option>
                          {(series.aggregation === 'COUNT' ? fields : getNumericFields()).map(field => (
                            <option key={field.name} value={field.name}>
                              {field.label || field.name} {field.type ? `(${field.type})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="text-2xl text-gray-400 dark:text-gray-500 mt-6">)</div>
                    </div>
                    {series.aggregation && series.field && (
                      <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded px-3 py-2">
                        <div className="text-xs font-medium text-teal-800 dark:text-teal-200 mb-1">
                          Series Expression:
                        </div>
                        <div className="text-sm font-mono text-teal-900 dark:text-teal-100">
                          {series.aggregation}({series.field})
                        </div>
                        <div className="text-xs text-teal-600 dark:text-teal-400 mt-1">
                          Grouped by: {config.xAxis || 'category field'}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Label (for chart display) <span className="text-teal-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={series.label || ''}
                        onChange={(e) => handleUpdateSeries(series.id, 'label', e.target.value)}
                        placeholder={series.aggregation && series.field ? `${series.aggregation}(${series.field})` : 'Series label'}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        This label will appear in the chart legend. Leave empty to auto-generate: {series.aggregation || 'COUNT'}({series.field || 'field'})
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={series.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                          onChange={(e) => handleUpdateSeries(series.id, 'color', e.target.value)}
                          className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                          title="Select color"
                        />
                        <input
                          type="text"
                          value={series.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                          onChange={(e) => handleUpdateSeries(series.id, 'color', e.target.value)}
                          placeholder="#3B82F6"
                          pattern="^#[0-9A-Fa-f]{6}$"
                          className="flex-1 px-2 py-1.5 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                        />
                        <button
                          type="button"
                          onClick={() => handleUpdateSeries(series.id, 'color', DEFAULT_COLORS[index % DEFAULT_COLORS.length])}
                          className="px-2 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                          title="Reset to default"
                        >
                          Reset
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Color for this series in the chart
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Grouping Configuration */}
      {!isPieChart && config.xAxis && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Grouping (optional)
          </label>
          <select
            value={config.grouping || ''}
            onChange={(e) => onChange({ ...config, grouping: e.target.value || null })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="">No Grouping</option>
            {getCategoricalFields()
              .filter(f => f.name !== config.xAxis)
              .map(field => (
                <option key={field.name} value={field.name}>
                  {field.label || field.name} ({field.type})
                </option>
              ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Group data by an additional field (e.g., by month, by category)
          </p>
        </div>
      )}

      {/* Sorting Configuration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Sorting
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <select
              value={config.sorting?.field || ''}
              onChange={(e) => onChange({
                ...config,
                sorting: {
                  ...config.sorting,
                  field: e.target.value || null
                }
              })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="">No Sorting</option>
              {fields.map(field => (
                <option key={field.name} value={field.name}>
                  {field.label || field.name}
                </option>
              ))}
            </select>
          </div>
          {config.sorting?.field && (
            <div>
              <select
                value={config.sorting?.direction || 'asc'}
                onChange={(e) => onChange({
                  ...config,
                  sorting: {
                    ...config.sorting,
                    direction: e.target.value
                  }
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Sort the data by a field before displaying
        </p>
      </div>

      {/* Filters */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Filters (optional)
        </label>
        <FilterBuilder
          entityName={entityName}
          filters={config.filters || []}
          onChange={(filters) => onChange({ ...config, filters })}
        />
      </div>

      {/* Chart Display Options */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Display Options
        </label>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.showDataLabels !== false}
              onChange={(e) => onChange({ ...config, showDataLabels: e.target.checked })}
              className="mr-2 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Data Labels
            </span>
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              Show values on chart elements
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.showAxisLabels !== false}
              onChange={(e) => onChange({ ...config, showAxisLabels: e.target.checked })}
              className="mr-2 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Axis Labels
            </span>
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              Show axis labels and tick marks
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default ChartEditor;

